import { Button, cn, Divider, RiDownloadLine, Stack } from 'component-library';
import i18next from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { OrganizationContract } from '@/api/rest/resources/types/organization';
import { Body, Header } from '@/components/widgets/a/Text/Text';
import { useContractDownload } from '@/hooks/useContractDownload';

type ContractCardProps = {
  contract: OrganizationContract;
  className?: string;
};

export const ContractCard = ({ contract, className }: ContractCardProps) => {
  const { downloadContract } = useContractDownload();

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    downloadContract(contract.id);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18next.language);
  };

  return (
    <Stack
      data-testid='contract-card'
      className={cn(
        'rounded-3xl border border-neutral-black-12 p-6 transition-all duration-200 gap-4 justify-between',
        'w-full sm:w-[380px] h-auto sm:h-[320px]',
        className,
      )}
    >
      <ContractThumbnail />
      <ContractInfo contract={contract} formatDate={formatDate} />
      <Divider />
      <Stack direction='row' className='justify-end gap-2'>
        <DownloadAction onDownload={handleDownload} />
      </Stack>
    </Stack>
  );
};

const ContractThumbnail = () => {
  const { t } = useTranslation();

  return (
    <Stack data-testid='contract-card-thumbnail' className='h-32 items-center justify-center'>
      <div className='w-24 h-32 bg-neutral-black-8 rounded flex items-center justify-center p-2'>
        <span className='text-neutral-black-40 text-xs text-center font-medium leading-tight'>
          {t('contracts.thumbnail.title')}
        </span>
      </div>
    </Stack>
  );
};

const ContractInfo = ({
  contract,
  formatDate,
}: {
  contract: OrganizationContract;
  formatDate: (date: string) => string;
}) => {
  return (
    <Stack data-testid='contract-card-info' spacing={2}>
      <Header text={contract.name} className='line-clamp-2 text-neutral-black-80' />
      <Body text={formatDate(contract.created_at)} className='text-neutral-black-60' />
    </Stack>
  );
};

const DownloadAction = ({ onDownload }: { onDownload: (e: React.MouseEvent) => void }) => {
  return (
    <Button
      onClick={onDownload}
      variant='text'
      size='small'
      aria-label='Download contract'
      data-testid='contract-download-button'
    >
      <RiDownloadLine className='text-primary-100' />
    </Button>
  );
};
